import type {ReactNode} from 'react';
import {AppLink} from '@client/pages/public/enroll/components/appDownload';

/**
 * Minimal inline-markup renderer for config-authored copy. Wrap the literal in a tag and the href
 * is inferred from the inner text — authors never retype/normalize a URL, phone, or email:
 *
 *   <url>ctpa.com</url>            → https://ctpa.com (opens in a new tab)
 *   <tel>1-888-820-5687</tel>      → tel:+18888205687
 *   <email>help@example.com</email> → mailto:help@example.com
 *   <app>Clever Health app</app>   → blue link that opens the download-the-app modal
 *
 * Everything outside the tags renders as plain text. Reusable anywhere config copy needs links.
 */

const TAG_PATTERN = /<(url|tel|email|app)>([\s\S]*?)<\/\1>/g;
const LINK_CLASS = 'text-blue-600 underline underline-offset-2 hover:text-blue-700';

function urlHref(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function telHref(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  if (digits.length === 10) return `tel:+1${digits}`;
  return `tel:+${digits}`;
}

export function renderRichText(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  TAG_PATTERN.lastIndex = 0;

  for (let match = TAG_PATTERN.exec(text); match !== null; match = TAG_PATTERN.exec(text)) {
    const [full, tag, value] = match;
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));

    if (tag === 'app') {
      nodes.push(<AppLink key={key++}>{value}</AppLink>);
    } else if (tag === 'url') {
      nodes.push(
        <a key={key++} href={urlHref(value)} target='_blank' rel='noreferrer' className={LINK_CLASS}>
          {value}
        </a>,
      );
    } else if (tag === 'tel') {
      nodes.push(
        <a key={key++} href={telHref(value)} className={LINK_CLASS}>
          {value}
        </a>,
      );
    } else {
      nodes.push(
        <a key={key++} href={`mailto:${value}`} className={LINK_CLASS}>
          {value}
        </a>,
      );
    }

    lastIndex = match.index + full.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}
